import {
  cloneElement,
  createContext,
  isValidElement,
  ReactNode,
  useContext,
  useEffect,
} from 'react';
import { useQuery } from '@tanstack/react-query';

import { tenantQueryOptions, TenantConfig } from '@lib/query/options/tenant';

interface TenantContextValue {
  tenant: TenantConfig | null;
  isLoading: boolean;
  error: Error | null;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function TenantProvider({
  children,
  fallback,
}: TenantProviderProps) {
  const { data, isLoading, error } = useQuery(tenantQueryOptions());

  // Apply theme class to body element based on tenant type
  useEffect(() => {
    if (data?.theme) {
      document.body.classList.add(data.theme);
      return () => {
        document.body.classList.remove(data.theme);
      };
    }
  }, [data?.theme]);

  // Clone fallback element to pass visible prop for fade transition
  const loaderElement =
    fallback && isValidElement<{ visible?: boolean }>(fallback)
      ? cloneElement(fallback, { visible: isLoading })
      : fallback;

  return (
    <TenantContext.Provider
      value={{
        tenant: data ?? null,
        isLoading,
        error: error as Error | null,
      }}
    >
      {children}
      {loaderElement}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant configuration from TenantProvider.
 *
 * @returns Object containing:
 * - `tenant`: Current tenant config, or null while loading
 * - `isLoading`: True while fetching tenant data
 * - `error`: Error object if fetch failed (unlikely due to infinite retry)
 *
 * @throws Error if used outside of TenantProvider
 *
 * @note The tenant query is configured with `retry: true`, meaning it will
 * keep retrying indefinitely until successful. The `error` state is therefore
 * unlikely to occur in practice - the loader will remain visible during retries.
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
}
